/**
 * ============================================
 * ScheduleRow.tsx — Строка расписания автобуса
 * ============================================
 * Отображает один рейс в таблице расписания.
 * Оптимизирован через React.memo — перерендеривается
 * только при изменении данных строки.
 */

import React from 'react';
import type { BusScheduleItem, BusStatus } from './useSchedule';

// ============================================
// Props
// ============================================

interface ScheduleRowProps {
  /** Данные рейса */
  item: BusScheduleItem;
  /** Флаг — была ли строка обновлена (для анимации) */
  isUpdated?: boolean;
  /** Флаг — новая ли строка (для анимации появления) */
  isNew?: boolean;
  /** Флаг активного рейса для автофокуса */
  isActive?: boolean;
}

// ============================================
// Хелперы
// ============================================

/**
 * Форматирует статус для отображения
 */
function formatStatus(status: BusStatus, delayMinutes?: number): string {
  switch (status) {
    case 'scheduled':
      return 'ПО РАСПИСАНИЮ';
    case 'boarding':
      return 'ПОСАДКА';
    case 'departed':
      return 'ОТПРАВЛЕН';
    case 'delayed':
      return delayMinutes ? `+${delayMinutes} МИН` : 'ЗАДЕРЖКА';
    case 'cancelled':
      return 'ОТМЕНЁН';
    default:
      return String(status).toUpperCase();
  }
}

/**
 * Возвращает CSS-класс для статуса
 */
function getStatusClass(status: BusStatus): string {
  return `tv-status tv-status--${status}`;
}

// ============================================
// Компонент
// ============================================

/**
 * Строка таблицы расписания.
 * Отображает время, маршрут, направление, платформу и статус.
 * Обёрнут в React.memo для предотвращения лишних ререндеров.
 */
export const ScheduleRow: React.FC<ScheduleRowProps> = React.memo(
  function ScheduleRow({ item, isUpdated = false, isNew = false, isActive = false }) {
    const statusText = formatStatus(item.status, item.delayMinutes);
    const statusClass = getStatusClass(item.status);

    // Классы строки для анимаций
    const rowClasses = [
      'tv-row',
      isNew ? 'tv-row--enter' : '',
      isUpdated ? 'tv-row--updated' : '',
      isActive ? 'tv-row--active' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={rowClasses} role="row" aria-label={`Рейс ${item.route} в ${item.time}`}>
        {/* Время отправления */}
        <div className="tv-row__cell tv-row__time" role="cell" data-row-id={item.id}>
          {item.time}
        </div>

        {/* Номер маршрута (цветной badge) */}
        <div className="tv-row__cell tv-row__route" role="cell">
          <span
            className="tv-route-badge"
            style={{ backgroundColor: item.routeColor }}
            aria-label={`Маршрут ${item.route}`}
          >
            {item.route}
          </span>
        </div>

        {/* Направление */}
        <div className="tv-row__cell tv-row__direction" role="cell" title={item.direction}>
          {item.direction}
        </div>

        {/* Платформа */}
        <div className="tv-row__cell tv-row__platform" role="cell">
          {item.platform}
        </div>

        {/* Статус с цветовой индикацией */}
        <div className="tv-row__cell tv-row__status" role="cell">
          <span className={statusClass} aria-label={`Статус: ${statusText}`}>
            {statusText}
          </span>
        </div>
      </div>
    );
  },
  /**
   * Кастомное сравнение пропсов для React.memo.
   * Перерендериваем только если данные реально изменились.
   */
  (prevProps, nextProps) => {
    const prev = prevProps.item;
    const next = nextProps.item;

    return (
      prev.id === next.id &&
      prev.time === next.time &&
      prev.route === next.route &&
      prev.routeColor === next.routeColor &&
      prev.direction === next.direction &&
      prev.platform === next.platform &&
      prev.status === next.status &&
      prev.delayMinutes === next.delayMinutes &&
      prevProps.isUpdated === nextProps.isUpdated &&
      prevProps.isNew === nextProps.isNew &&
      prevProps.isActive === nextProps.isActive
    );
  }
);

// Экспорт типов
export type { ScheduleRowProps };
